import { EXTRACT_OCR_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import {
  ExtractOCRResult,
  ExtractOCRStatus,
  ExtractOCRStatusResponse,
  FileCategory,
  FileState,
} from "core/types";
// #TODO: move useHandleFileDownload / isFileHISStateful into core or inject from app
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { useHandleFileDownload } from "dsl/ecosystems/PatientFiles/useDownloadFile";
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { useCallback, useEffect, useState } from "react";

type OCRResultsByFileId = Record<string | number, ExtractOCRResult>;

export const useExtractOCR = ({
  auctionId,
  careseekerId,
  externalId,
  ocrStatus,
  onComplete,
  patientId,
}: {
  careseekerId: number | undefined;
  patientId: number;
  auctionId: number | undefined;
  externalId: string | undefined;
  onComplete: () => Promise<ExtractOCRStatusResponse | null>;
  ocrStatus: ExtractOCRStatusResponse | null;
}) => {
  const [results, setResults] = useState<OCRResultsByFileId>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [extractingAllFiles, setExtractingAllFiles] = useState(false);

  const [processingFileIds, setProcessingFileIds] = useState<
    Array<string | number>
  >([]);

  const handleDownload = useHandleFileDownload({
    externalId: externalId ?? "",
    shouldOpenInNewTab: false,
  });

  const handleOcrForFile = useCallback(
    async ({
      file,
      fileCategory,
      id,
    }: {
      file: File;
      fileCategory: FileCategory;
      id: number;
    }) => {
      setProcessingFileIds((prev) => [...prev, id]);

      try {
        if (!careseekerId || !patientId || !auctionId) {
          throw new Error(
            "careseekerId, patientId or auctionId is not defined",
          );
        }
        const ocrResult = await aiApi.extract.ocr({
          auctionId,
          careseekerId,
          file,
          fileCategory,
          fileId: String(id),
          patientId,
        });

        setResults((prev) => ({
          ...prev,
          [id]: ocrResult,
        }));

        await onComplete();

        return ocrResult;
      } finally {
        setProcessingFileIds((prev) => prev.filter((fileId) => fileId !== id));
      }
    },
    [auctionId, setProcessingFileIds, setResults, onComplete],
  );

  const downloadAndRunOcr = useCallback(
    async (file: FileState): Promise<ExtractOCRResult> => {
      if (!careseekerId || !patientId || !auctionId) {
        throw new Error("careseekerId, patientId or auctionId is not defined");
      }

      const downloadResult = await handleDownload(file);
      if (!downloadResult) {
        throw new Error("Failed to download file - no result returned");
      }
      if (!downloadResult.pdfBytes) {
        throw new Error("No PDF bytes returned from download");
      }

      const ocrResult = await aiApi.extract.ocr({
        auctionId,
        careseekerId,
        file: new File(
          [downloadResult.pdfBytes as BlobPart],
          downloadResult.fileName,
          {
            type: "application/pdf",
          },
        ),
        fileCategory: isFileHISStateful(file)
          ? file.document_category_number
          : file.category,
        fileId: String(file.id),
        patientId,
      });

      setResults((prev) => ({
        ...prev,
        [file.id]: ocrResult,
      }));

      return ocrResult;
    },
    [auctionId, careseekerId, patientId, handleDownload],
  );

  const handleDownloadAndRunOcr = useCallback(
    async ({
      extractAllFiles,
      files,
    }: {
      files: FileState[];
      extractAllFiles?: boolean;
    }) => {
      // Files that should be excluded from extraction
      const excludedStatuses = [
        EXTRACT_OCR_STATUS.EXTRACTED,
        EXTRACT_OCR_STATUS.PROCESSING,
      ] as ExtractOCRStatus[];

      const excludedFileIds = ocrStatus?.documents
        .filter((doc) => excludedStatuses.includes(doc.status))
        .map((doc) => doc.file_id);

      const filesToExtract = files.filter(
        (file) => !excludedFileIds?.includes(String(file.id)),
      );
      if (!filesToExtract.length) return;

      setErrors([]);
      setExtractingAllFiles(!!extractAllFiles);
      setProcessingFileIds((prev) => [
        ...prev,
        ...filesToExtract.map((f) => f.id),
      ]);

      try {
        await Promise.all(
          filesToExtract.map(async (file) => {
            try {
              await downloadAndRunOcr(file);
            } catch (err) {
              const message = getErrorMessage(err);
              console.error(
                `error with extract ocr for file ${file.id}`,
                message,
              );
              setErrors((prev) => [...prev, message]);
            }
          }),
        );

        await onComplete();
      } finally {
        setProcessingFileIds((prev) =>
          prev.filter((id) => !filesToExtract.some((file) => file.id === id)),
        );
        setExtractingAllFiles(false);
      }
    },
    [downloadAndRunOcr, onComplete],
  );

  useEffect(() => {
    return () => {
      setProcessingFileIds([]);
      setErrors([]);
      setExtractingAllFiles(false);
    };
  }, []);

  return {
    errors,
    extractingAllFiles,
    handleDownloadAndRunOcr,
    handleOcrForFile,
    processingFileIds,
    results,
  };
};

export type UseExtractOCRReturn = ReturnType<typeof useExtractOCR>;
